import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Router, UrlTree } from '@angular/router';
import { catchError, filter, finalize, first, fromEvent, lastValueFrom, Observable, of, tap, throwError, timeout } from 'rxjs';
import { map } from 'rxjs/operators';
import { Log, User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { environment } from '../../environments/environment';
import { HttpErrorInterceptor } from '../interceptors/http-error-interceptor/http-error.interceptor';
import { IAuthService } from './auth.types';

@Injectable({
    providedIn: 'root'
})
export class AuthService implements IAuthService {
    private readonly identityServerStorageKey: string = 'identityServerUri';

    private _identityServerEndpoint: string;
    get identityServerEndpoint(): string {
        return this._identityServerEndpoint;
    }

    private _newIdentityServerEndpoint: string | null;
    get newIdentityServerEndpoint(): string | null {
        return this._newIdentityServerEndpoint;
    }

    private _companyUri: string | null;
    get companyUri(): string {
        return this._companyUri!;
    }

    private _currentUser: User | null;
    get currentUser(): User {
        return this._currentUser!;
    }
    set currentUser(user: User | null) {
        this._currentUser = user;
    }

    private mgr: UserManager;
    get isReady(): boolean {
        return !!(this.identityServerEndpoint && this.mgr);
    }

    constructor(private readonly http: HttpClient,
        private readonly router: Router) {
        if (environment.production) {
            Log.reset();
            Log.setLevel(Log.NONE);
        } else {
            Log.setLogger(console);
            Log.setLevel(Log.DEBUG);
        }
    }

    async registerIdentityServerEndpoint(companyUri: string): Promise<UrlTree | void> {
        let server: string | null = null;
        if (environment.production) {
            if (companyUri) {
                const funcAppUrl = environment.azureFunctionKeyUrl;
                try {
                    server = await lastValueFrom(this.http.get<string>(`${funcAppUrl}&uri=${companyUri}`));
                } catch (error) {
                    if (error instanceof HttpErrorResponse) {
                        if (error?.status === 0 && error?.statusText === 'Unknown Error') {
                            return this.router.parseUrl(`${companyUri}/network-access-guide`);
                        }
                    }
                }
            } else {
                server = environment.defaultIdentityServer;
            }
        }

        this._identityServerEndpoint = environment.identityServerEndpoint(server);
        localStorage.setItem(this.identityServerStorageKey, this._identityServerEndpoint);
    }

    initUserManager(): void {
        this.mgr = new UserManager({
            authority: this.identityServerEndpoint,
            client_id: environment.client_id,
            response_type: 'code',
            client_secret: 'QuantiumAuthSecretKey',
            scope: environment.scope,
            loadUserInfo: true,
            monitorSession: true,
            userStore: new WebStorageStateStore({ store: localStorage }),
            redirect_uri: `${environment.myHostname}/${this.companyUri}/callback`,
            post_logout_redirect_uri: `${environment.myHostname}/${this.companyUri}`,
            silent_redirect_uri: `${environment.myHostname}/${this.companyUri}/silent-refresh?companyUri=${this.companyUri}`,
            automaticSilentRenew: true,
            validateSubOnSilentRenew: true,
            silentRequestTimeoutInSeconds: 300,
            accessTokenExpiringNotificationTimeInSeconds: 60
        });

        this.mgr.events.addUserLoaded(user => { this.currentUser = user; });
    }

    checkCompanyUri(uri: string): Observable<boolean> {
        if (!uri) {
            this._companyUri = null;
            return of(false);
        }

        const url = `${this.identityServerEndpoint}/QuantiumPortal/${uri}`;
        return this.http.get(`${url}`, {
            responseType: 'text',
            context: new HttpContext().set(HttpErrorInterceptor.SKIP, true)
        }).pipe(
            map(result => !!result),
            tap(found => this._companyUri = found ? uri : null),
            catchError(err => {
                this._companyUri = null;
                return throwError(err);
            })
        );
    }

    hasAccessToCompanyUri(): Observable<boolean> {
        const url = `${this.identityServerEndpoint}/QuantiumPortal/verify/${this.companyUri}`;
        return this.http.get<boolean>(`${url}`, {
            context: new HttpContext().set(HttpErrorInterceptor.SKIP, true)
        });
    }

    needSignInToClientUri(): Observable<boolean> {
        const url = `${this.identityServerEndpoint}/QuantiumPortal/verifyMultipleClients/${this.companyUri}`;
        return this.http.get<boolean>(`${url}`);
    }

    hasMultipleClients(): boolean {
        if (!this._currentUser) {
            return false;
        }

        return this._currentUser.profile['hasMultipleClientsForPortal'] === true;
    }

    canSwitchRole(): boolean {
        if (!this._currentUser) {
            return false;
        }

        return this._currentUser.profile['canSwitchGPLP'] === true;
    }

    async canAccessThirdPartyCookies(): Promise<boolean> {
        const frame = document.createElement('iframe');
        frame.height = '0';
        frame.width = '0';
        frame.className = 'invisible position-fixed border-0';
        frame.sandbox.value = 'allow-scripts allow-same-origin';
        frame.src = `${this.identityServerEndpoint}/account/checkcookieavailability`;

        document.body.appendChild(frame);

        const obs = fromEvent<MessageEvent>(window, 'message')
            .pipe(
                filter(({ origin, source }) => origin === this.identityServerEndpoint && source === frame.contentWindow),
                map(({ origin, source, data }) => data === 'ready' ? (source as WindowProxy).postMessage({ eventId: 'qf.checkCookieAvailability' }, origin) : data),
                first(status => status && typeof status === 'object'),
                map(({ isCookieAvailable }) => isCookieAvailable as boolean),
                timeout(3e4), // 30 secs
                catchError(() => {
                    console.error('Connection for checking cookie availability timed out.');
                    return of(false);
                }),
                finalize(() => frame.remove())
            );

        return await lastValueFrom(obs);
    }

    async tryGetUserInStorage(): Promise<User | null> {
        await this.mgr.clearStaleState();
        return this.currentUser = await this.mgr.getUser();
    }

    async isUserInStorageSameToAuth(): Promise<boolean> {
        try {
            const session = await this.mgr.querySessionStatus();
            if (!session || session.sub !== this.currentUser?.profile?.sub) {
                throw new Error('Credential in the current session is not the same as in storage.');
            }

            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    }

    listenToSignedOutEvent(): void {
        this.mgr.events.addUserSignedOut(() => this.signOut());
    }

    listenToUserSessionChangedEvent(): void {
        this.mgr.events.addUserSessionChanged(() => this.signIn());
    }

    async signIn(redirectUrl?: string): Promise<void> {
        await this.mgr.signinRedirect({
            extraQueryParams: { companyUri: this.companyUri },
            state: redirectUrl
        });
    }

    async signinSilent(): Promise<User | null> {
        return await this.mgr.signinSilent({
            extraQueryParams: { companyUri: this.companyUri },
        });
    }

    async signOut(): Promise<void> {
        localStorage.removeItem(this.identityServerStorageKey);

        await this.mgr.signoutRedirect({
            id_token_hint: this.currentUser?.id_token,
            extraQueryParams: { companyUri: this.companyUri }
        });
    }

    /*
     * @return Redirect URL passed to sign-in.
     */
    async signinRedirectCallback(url?: string): Promise<string> {
        this.currentUser = await this.mgr.signinRedirectCallback(url);
        return this.currentUser.state as string;
    }

    async signinSilentCallback(url?: string): Promise<void> {
        await this.mgr.signinSilentCallback(url);
    }

    getAccessToken(): string {
        return this.currentUser ? this.currentUser.access_token : '';
    }

    getAuthorizationToken(): string {
        return this.currentUser ? `${this.currentUser.token_type} ${this.getAccessToken()}` : '';
    }

    isAccessTokenExpiring(): boolean {
        const renewInSecs = this.mgr.settings.accessTokenExpiringNotificationTimeInSeconds;
        return !this.currentUser || this.currentUser.expired || this.currentUser.expires_in! < renewInSecs;
    }

    fallback(): void {
        const fallbackEndpoint = environment.fallbackEndpoint(this._identityServerEndpoint);
        if (fallbackEndpoint) {
            this._newIdentityServerEndpoint = this._identityServerEndpoint;
            this._identityServerEndpoint = fallbackEndpoint;
        }
    }
}
